<template>
    <PageWrapper title="Activity History">
        <div v-if="showFullPageError" class="GlobalActivityHistoryView__error">
            <p>We could not load activity history.</p>
            <NewButton size="sm" variant="outline" color="secondary" @click="reload">
                Retry
            </NewButton>
        </div>
        <template v-else>
            <div v-if="status.error" class="GlobalActivityHistoryView__inlineError">
                <p>We could not load more activity history.</p>
                <NewButton size="sm" variant="outline" color="secondary" @click="retryNext">
                    Retry
                </NewButton>
            </div>
            <ActivityHistoryTable
                :entries="status.list"
                :loading="status.loading"
                :finished="status.finished"
                :filters="filters"
                :enum-labels="enumLabels"
                :activity-types="globalActivityTypes"
                :show-tags="false"
                :show-view-column="false"
                @scroll-end="loadNext"
                @filter-change="applyFilters"
            />
        </template>
    </PageWrapper>
</template>

<style lang="scss">
    .GlobalActivityHistoryView {
        &__error {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 12px;
            padding: 32px;
            text-align: center;
            color: var(--eidDangerRed);
        }

        &__inlineError {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 0;
            color: var(--eidDangerRed);
        }
    }
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { useRbacStore } from '@evidentid/dashboard-commons/modules/role-base-access-control/store';
    import {
        ActivityHistoryQuery,
        ActivityType,
        GLOBAL_ACTIVITY_TYPES,
    } from '@evidentid/tprm-portal-lib/models/activity-history';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import NewButton from '@/components/new-button/NewButton.vue';
    import PageWrapper from '@/components/page-wrapper/PageWrapper.vue';
    import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
    import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
    import ActivityHistoryTable
        from '@/modules/entity-events/components/ActivityHistoryTable/ActivityHistoryTable.vue';
    import { ActivityHistoryFilters } from '@/modules/entity-events/models/ActivityHistoryFilters.model';
    import { normalizeActivityHistoryFilters } from '@/modules/entity-events/utils/normalizeActivityHistoryFilters';
    import { GlobalEventsStatus, useGlobalEventsStore } from '@/modules/global-events/store';

    export default defineComponent({
        name: 'GlobalActivityHistoryView',
        components: { PageWrapper, NewButton, ActivityHistoryTable },
        data() {
            return {
                globalActivityTypes: GLOBAL_ACTIVITY_TYPES as ActivityType[],
            };
        },
        computed: {
            rpName(): string {
                return (this as any).$rp?.current || '';
            },
            status(): GlobalEventsStatus {
                return useGlobalEventsStore().globalEvents[this.rpName]
                    || { loading: false, finished: false, list: [], filters: {}, error: false };
            },
            filters(): ActivityHistoryFilters {
                return normalizeActivityHistoryFilters(this.status.filters);
            },
            enumLabels(): CategorizedEnumLabels {
                return useDashboardStore().categorizedEnumLabels || {};
            },
            // A failed pagination append (status.error with rows already in status.list) should
            // not blank the page: only an empty list means there is nothing to fall back to, so
            // that's the only case the full-page error state applies to.
            showFullPageError(): boolean {
                return this.status.error && this.status.list.length === 0;
            },
        },
        watch: {
            '$rp.current': {
                handler(rpName: string, prevRpName: string) {
                    if (rpName && rpName !== prevRpName) {
                        useRbacStore().validateRbac(
                            [ RbacFeature.configurationActivityHistory ],
                        );
                    }
                    if (rpName) {
                        useGlobalEventsStore().loadGlobalEvents({ rpName, filters: {} });
                    }
                    if (prevRpName) {
                        useGlobalEventsStore().clearGlobalEvents({ rpName: prevRpName });
                    }
                },
            },
        },
        mounted() {
            if (this.rpName) {
                useRbacStore().validateRbac(
                    [ RbacFeature.configurationActivityHistory ],
                );
                useGlobalEventsStore().loadGlobalEvents({ rpName: this.rpName, filters: {} });
            }
        },
        beforeUnmount() {
            if (this.rpName) {
                useGlobalEventsStore().clearGlobalEvents({ rpName: this.rpName });
            }
        },
        methods: {
            applyFilters(rawFilters: ActivityHistoryQuery) {
                if (!this.rpName) {
                    return;
                }
                // Normalized first so a query missing entryType/tags/causedBy (the shape the
                // filters panel emits on Clear) does not crash on a missing array. Tags are then
                // dropped rather than forwarded: global entries never carry any, and with
                // show-tags false the filters panel never offers a tags field to populate one.
                const filters = normalizeActivityHistoryFilters(rawFilters);
                const query: ActivityHistoryQuery = {
                    ...(filters.entryType.length ? { entryType: filters.entryType } : {}),
                    ...(filters.causedBy ? { causedBy: filters.causedBy } : {}),
                };
                useGlobalEventsStore().loadGlobalEvents({ rpName: this.rpName, filters: query });
            },
            loadNext() {
                if (!this.rpName) {
                    return;
                }
                useGlobalEventsStore().loadNextGlobalEvents({ rpName: this.rpName });
            },
            // The inline error banner's Retry button goes through this rather than loadNext:
            // loadNextGlobalEvents no longer re-fires on its own once a page has failed, so
            // resuming pagination needs the action that clears error before it delegates.
            retryNext() {
                if (!this.rpName) {
                    return;
                }
                useGlobalEventsStore().retryNextGlobalEvents({ rpName: this.rpName });
            },
            reload() {
                if (!this.rpName) {
                    return;
                }
                useGlobalEventsStore().loadGlobalEvents({ rpName: this.rpName, filters: this.status.filters });
            },
        },
    });
</script>
